import { Injectable } from '@angular/core';
import { EventPipelineService } from '@bootstrap/services/event-pipeline.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { PatientStore } from '@modules/patient/state/patient-store';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { PatientCardStateService } from '../../services/patient-card-state.service';
import { Observable, merge, map, tap, combineLatest, withLatestFrom } from 'rxjs';
import {
	EventName,
	PatientAppError,
	PatientAppReady,
	PatientUpdated,
	PatientCleared,
	PatientSaved
} from '@itero/itero-client-communication';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { ViewModeEnum } from '@shared/models/consts';
import { RxStatusEnum } from '@shared/models/enums/rx-status.enum';
import { RxStatusService } from '@core/services/rx-status.service';
import { HostPlatformService } from '@shared/services/host-platform.service';

/** Patient type for iTero-native patients (expected by Rx-MS) */
const PATIENT_TYPE_ITERO = 1;

@Injectable()
export class PatientCardFacade {
	private readonly moduleName = 'PatientCardFacade';

	private readonly isOnlyEditAllowed$: Observable<boolean> = combineLatest([
		this.shellQuery.viewMode$,
		this.rxScanStatusServiceService.status$,
		this.shellQuery.clonedRx$
	]).pipe(
		map(([viewMode, scanStatus, isCloned]) => {
			return viewMode === ViewModeEnum.Summary || scanStatus === RxStatusEnum.Scanned || !!isCloned;
		})
	);

	private readonly apiEndpointDependingOnHost$ = this.shellQuery.apiEndpoint$.pipe(
		withLatestFrom(this.shellQuery.patientAppApiEndpoint$, this.hostPlatformService.isScanner$),
		map(([apiEndpoint, patientAppApiEndpoint, isHostPlatformScanner]) => {
			return isHostPlatformScanner ? patientAppApiEndpoint ?? apiEndpoint : null;
		})
	);

	readonly isPatientAppReady$ = this.patientCardState.isPatientCardReady$;

	constructor(
		private readonly eventPipeline: EventPipelineService,
		private readonly patientStore: PatientStore,
		private readonly shellQuery: ShellQuery,
		private readonly patientQuery: PatientQuery,
		private readonly logger: LoggerService,
		private readonly patientCardState: PatientCardStateService,
		private readonly rxScanStatusServiceService: RxStatusService,
		private readonly hostPlatformService: HostPlatformService
	) {}

	appParameters$: Observable<Record<string, unknown> | undefined> = combineLatest({
		patient: this.patientQuery.patient$,
		isOnlyEditAllowed: this.isOnlyEditAllowed$,
		apiEndpoint: this.apiEndpointDependingOnHost$
	}).pipe(
		map(({ patient, isOnlyEditAllowed, apiEndpoint }) => ({
			apiEndpoint,
			patientId: patient?.UID,
			...(isOnlyEditAllowed && { mode: 'edit' })
		}))
	);

	listenIncomingEvents(): Observable<unknown> {
		const waitReadiness = this.eventPipeline
			.fromEvent<PatientAppReady>(EventName.patientAppReady)
			.pipe(tap(() => this.patientStore.update({ isPatientAppConfigured: true })));

		return merge(
			waitReadiness,
			this.eventPipeline.fromEvent<PatientAppError>(EventName.patientAppError).pipe(tap(event => this.onPatientAppError(event))),
			this.eventPipeline.fromEvent<PatientUpdated>(EventName.patientUpdated).pipe(tap(event => this.onPatientUpdated(event.data))),
			this.eventPipeline.fromEvent<PatientSaved>(EventName.patientSaved).pipe(tap(event => this.onPatientSaved(event.data))),
			this.eventPipeline.fromEvent<PatientCleared>(EventName.patientCleared).pipe(tap(() => this.onPatientCleared()))
		);
	}

	// ─── Event Handlers → Store Updates ───────────────────────────────
	private onPatientAppError(event: PatientAppError): void {
		this.logger.error(`Patient MFE error: ${event.data.errorCode}`, { module: this.moduleName });
	}

	private onPatientUpdated(data: PatientUpdated['data']): void {
		const dto = this.mapToPatientDto(data);

		this.patientStore.updatePatient(dto, this.shellQuery.dateFormat, this.shellQuery.regulatoryDOBMask);
		this.patientStore.update({
			hasUnsavedChanges: data.hasUnsavedChanges ?? false,
			isPatientValid: data.isValid ?? true
		});
	}

	private onPatientCleared(): void {
		this.patientStore.update({ patient: null, isPatientCleared: true, hasUnsavedChanges: false });
	}

	private onPatientSaved(data: PatientSaved['data']): void {
		if (!data.success) {
			this.logger.error('Patient save failed', { module: this.moduleName });
		} else {
			this.onPatientUpdated(data);
		}
	}

	// ─── Helpers ──────────────────────────────────────────────────────
	private mapToPatientDto(data: PatientUpdated['data'] | PatientSaved['data']): PatientModelDto {
		return {
			Id: data.id,
			UID: data.uid ?? '',
			FirstName: data.firstName ?? '',
			LastName: data.lastName ?? '',
			FullName: `${data.firstName ?? ''} ${data.lastName ?? ''}`,
			ChartNumber: data.chartNumber ?? '',
			DateOfBirth: data.dateOfBirth ?? '',
			Gender: data.gender,
			MI: data.mi,
			ZipCode: data.zipCode ?? '',
			AlignPatientId: data.alignPatientId ?? '',
			Type: data.type ?? PATIENT_TYPE_ITERO,
			...(data.lastScanDate ? { LastScanDate: data.lastScanDate } : {})
		};
	}
}
